import React, { useEffect, useRef } from 'react';
import matterportService from '../services/matterportService';
import navHintsService from '../services/navHintsService';
import { SIZE_PRESETS } from '../services/navHintsService';
import navDirectionSignService from '../services/navDirectionSignService';
import voiceoverService from '../services/voiceoverService';
import audioService from '../services/audioService';
import { useLanguage } from '../context/LanguageContext';
import { Z_INDEX } from '../const';
import deviceProfile from '../utils/deviceProfile';
import './NavigationHints.css';

const SIZE_MAP = Object.fromEntries(SIZE_PRESETS.map((s) => [s.value, s]));

// Localised wayfinding direction labels for type:'pill'. Bucketed into
// 4 cardinal wedges from the live screen-space angle:
//   Straight  : ±45°
//   Right     :  45° to 135°
//   Behind    : ±135°+
//   Left      : −45° to −135°
const PILL_DIRECTION_LABELS = {
  en: {
    straight: 'Straight',
    right: 'Right',
    behind: 'Behind',
    left: 'Left',
  },
  mr: {
    straight: 'सरळ',
    right: 'उजवीकडे',
    behind: 'मागे',
    left: 'डावीकडे',
  },
  hi: {
    straight: 'सीधे',
    right: 'दाएँ',
    behind: 'पीछे',
    left: 'बाएँ',
  },
};

/**
 * Renders editor-authored custom navigation hints in the Matterport scene.
 * Uses a requestAnimationFrame loop with direct DOM writes (no React
 * re-renders) for smooth 60fps tracking to world-space points.
 *
 * Three hint kinds are supported via `hint.type`:
 *   - 'nav'  — directional arrow, honours approachVector, non-interactive.
 *   - 'poi'  — omnidirectional marker, clickable; clicking opens a popup
 *              with the optional multi-language description.
 *   - 'pill' — wayfinding pill that shows live compass bearing + distance
 *              to the linked tour snapshot (`targetSid`). Omnidirectional
 *              and clickable — clicking flies the viewer to the target.
 */
const NavigationHints = ({ active }) => {
  const { language } = useLanguage();

  const customHostRef = useRef(null);
  const customNodesRef = useRef(new Map()); // id -> { root, label, arrow }
  const customHintsRef = useRef([]);

  // Editor live state — mirrored into refs so the rAF loop and DOM event
  // handlers can read it without re-binding on every change.
  const editorActiveRef = useRef(false);
  const editorSelectedIdRef = useRef(null);
  // Transient drag state. When set, suppresses click-to-navigate on the
  // node that was just dragged so releasing the mouse doesn't fly away.
  const dragStateRef = useRef({ id: null, justEnded: 0 });

  // Per-POI <audio> elements keyed by hint id. Lazily created the first
  // time the viewer enters the audio radius; kept around for the session.
  const audioNodesRef = useRef(new Map()); // id -> { el, src, playing }

  // Tracks which POI (if any) currently owns the auto-voiceover playback
  // so the rAF loop can pause it when the user steps outside the
  // proximity+facing window. `null` when no auto-voiceover is active.
  const autoVoiceoverHintIdRef = useRef(null);
  // Set of hint ids whose narration has played to natural completion
  // during the user's CURRENT visit to the trigger window. Cleared per
  // hint when the user leaves the window — so the next approach replays.
  // Without this, the rAF loop would retrigger playback the frame after
  // `onEnded` fires (user still inWindow, no longer owns playback).
  const autoVoiceoverFinishedRef = useRef(new Set());
  // Timestamp (performance.now()) at which the currently-owning POI
  // first fell OUTSIDE the (hysteresis-padded) hold window. 0 means the
  // owner is still inside the hold window. Used to debounce the exit
  // fade-out so that small camera jitters / momentary glances away
  // don't chop the narration into pause-then-resume.
  const autoVoiceoverExitAtRef = useRef(0);
  // When set, the next rAF tick will force-trigger auto-voiceover for
  // the closest autoVoiceover-enabled POI to the camera, bypassing the
  // strict facing-arc check and any "alreadyFinished" flag. Used by
  // LocationQuickNav (footer button) so a user-gesture jump to a
  // location starts narration the same way an organic approach does.
  // Shape: { expiresAt: number } | null
  const forceAutoVoiceoverRef = useRef(null);
  // Currently broadcast POI gallery hint id (or null when none in range).
  // Used to dedupe `poiGalleryActive` / `poiGalleryInactive` window
  // events so LocationGallery only re-renders on changes, not every
  // rAF tick.
  const activeGalleryHintIdRef = useRef(null);

  // Latest UI language mirrored into a ref so DOM event handlers wired
  // once per node (e.g. the speaker-icon click) always read the current
  // value rather than the language captured when the handler was bound.
  const languageRef = useRef(language);
  useEffect(() => { languageRef.current = language; }, [language]);

  // Subscribe to custom hints service — keep latest list in a ref for the rAF loop.
  useEffect(() => {
    const sync = () => { customHintsRef.current = navHintsService.getAll(); };
    sync();
    return navHintsService.subscribe(sync);
  }, []);

  // Mirror editor active + selected-id into refs. The renderer reads these
  // to decide whether to show the "move" cursor on the selected hint and
  // accept mouse drags to reposition it in-world.
  useEffect(() => {
    editorActiveRef.current = navHintsService.isEditorActive();
    editorSelectedIdRef.current = navHintsService.getEditorSelectedId();
    const offA = navHintsService.subscribeEditorActive((v) => {
      editorActiveRef.current = !!v;
    });
    const offS = navHintsService.subscribeEditorSelection((id) => {
      editorSelectedIdRef.current = id || null;
    });
    return () => { offA(); offS(); };
  }, []);

  // Begin a drag on hint `id`. Creates a full-screen overlay that captures
  // mousemove / mouseup events, and for each move raycasts the cursor
  // against the Matterport scene to produce an updated world position.
  // Only active when the editor is open and this hint is the selection.
  const beginDrag = (id, downEvent) => {
    const sdk = matterportService.state?.sdk;
    if (!sdk?.Renderer?.getWorldPositionData) return;
    const iframe = document.querySelector('iframe[title="Matterport"]')
      || document.querySelector('iframe');
    if (!iframe) return;
    const getRect = () => iframe.getBoundingClientRect();

    dragStateRef.current = { id, justEnded: 0 };

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = String(Z_INDEX.NAV_HINT_CUSTOM + 1);
    overlay.style.cursor = 'grabbing';
    overlay.style.background = 'transparent';
    document.body.appendChild(overlay);

    // Throttle: keep only the latest pending raycast to avoid queueing up
    // hundreds during fast mouse movement.
    let busy = false;
    let pending = null;
    const applyAt = async (clientX, clientY) => {
      if (busy) { pending = { clientX, clientY }; return; }
      busy = true;
      try {
        const r = getRect();
        const posData = await sdk.Renderer.getWorldPositionData({
          x: clientX - r.left,
          y: clientY - r.top,
        });
        if (posData?.position) {
          const { x, y, z } = posData.position;
          navHintsService.update(id, {
            position: {
              x: parseFloat(x.toFixed(3)),
              y: parseFloat(y.toFixed(3)),
              z: parseFloat(z.toFixed(3)),
            },
          });
        }
      } catch (_) { /* ignore transient raycast failures */ }
      busy = false;
      if (pending) {
        const p = pending; pending = null;
        applyAt(p.clientX, p.clientY);
      }
    };

    const onMove = (ev) => {
      ev.preventDefault();
      applyAt(ev.clientX, ev.clientY);
    };
    const onUp = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      overlay.removeEventListener('mousemove', onMove);
      overlay.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseup', onUp);
      try { overlay.remove(); } catch (_) {}
      dragStateRef.current = { id: null, justEnded: Date.now() };
    };
    overlay.addEventListener('mousemove', onMove);
    overlay.addEventListener('mouseup', onUp);
    // Also catch mouseup if the browser routes it outside the overlay.
    window.addEventListener('mouseup', onUp);

    // Seed an initial move so clicking without dragging still snaps to the
    // cursor position (useful for fine nudges).
    if (downEvent) applyAt(downEvent.clientX, downEvent.clientY);
  };

  // Clean up any created <audio> elements on unmount.
  useEffect(() => {
    const map = audioNodesRef.current;
    // When the global mute toggle flips ON, pause every proximity-audio
    // element we currently own. Without this, a hint whose audioRadius
    // the user is already inside keeps playing after mute — its <audio>
    // bypasses audioService's masterGain/AudioContext entirely. Flag the
    // entry as not-playing so the rAF loop will re-issue play() when the
    // user unmutes while still in range.
    const onMute = (e) => {
      if (!e?.detail?.muted) return;
      map.forEach((entry) => {
        try { entry.el.pause(); } catch (_) {}
        entry.playing = false;
      });
    };
    window.addEventListener('audioMuted', onMute);
    return () => {
      window.removeEventListener('audioMuted', onMute);
      map.forEach(({ el }) => {
        try { el.pause(); el.src = ''; el.remove(); } catch (_) {}
      });
      map.clear();
    };
  }, []);

  // rAF loop: project every authored hint into screen space each frame and
  // apply styles via direct DOM writes.
  useEffect(() => {
    if (!active) return undefined;

    let rafId = null;
    // POIs stay visible at all times — they project from the live camera
    // pose every frame, so during a FLY transition they simply track the
    // camera. Hiding/showing them on transition boundaries caused a
    // visible blink on every guided-tour Next.
    const onQuickNavArrival = () => {
      forceAutoVoiceoverRef.current = { expiresAt: performance.now() + 3000 };
    };
    window.addEventListener('quickNavArrival', onQuickNavArrival);

    // When the footer quick-nav (or guided tour) starts a per-stop
    // standalone narration via audioService.playTourNarration, mark
    // any POI hint whose voiceoverTagId matches as already-finished —
    // otherwise, after the narration ends the user is still standing
    // inside the POI's trigger window and proximity auto-VO would
    // fire again with the same audio, causing the narration to seem
    // to "restart". The finished marker is cleared automatically when
    // the user walks out of the trigger window, so re-entering later
    // can still play the narration on demand.
    const onNarrationStarted = (e) => {
      const key = e?.detail?.key;
      if (!key) return;
      const list = customHintsRef.current || [];
      for (const h of list) {
        if (h && h.voiceoverTagId === key) {
          autoVoiceoverFinishedRef.current.add(h.id);
        }
      }
    };
    window.addEventListener('narrationStarted', onNarrationStarted);

    // Tick interval by device tier:
    //   high (desktop / modern phone) — full RAF speed (60fps)
    //   mid  (recent mobile)         — ~30fps (33ms)
    //   low  (older/weaker mobile)   — ~20fps (50ms)
    let lastTickTime = 0;
    const TICK_INTERVAL =
      deviceProfile.tier === 'low' ? 50 :
      deviceProfile.tier === 'mid' ? 33 : 0;

    const tick = () => {
      // Throttle on mobile to reduce CPU/GPU load
      if (TICK_INTERVAL > 0) {
        const now = performance.now();
        if (now - lastTickTime < TICK_INTERVAL) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        lastTickTime = now;
      }

      const pose = matterportService.getCurrentPose();
      // ── Custom (editor-authored) hints ──────────────────────────────────
      const host = customHostRef.current;
      if (host && pose && pose.position) {
        const list = customHintsRef.current || [];
        const nodes = customNodesRef.current;
        const seen = new Set();

        // When a 'quickNavArrival' has armed the force flag, find the
        // closest autoVoiceover-enabled POI to the camera (within its
        // own radius) so only one POI takes ownership — not the first
        // one we happen to iterate over.
        const forceWindowTop = forceAutoVoiceoverRef.current;
        if (forceWindowTop && performance.now() >= forceWindowTop.expiresAt) {
          forceAutoVoiceoverRef.current = null;
        } else if (forceWindowTop && !forceWindowTop.targetHintId) {
          let bestId = null;
          let bestDist = Infinity;
          for (const h of list) {
            if (!h || h.visible === false) continue;
            if (h.type !== 'poi' || !h.autoVoiceover || !h.voiceoverTagId) continue;
            // Prefer trigger points (≤1 m); fall back to legacy radius.
            const tps = Array.isArray(h.triggerPoints) ? h.triggerPoints : [];
            let d = Infinity;
            if (tps.length > 0) {
              for (const tp of tps) {
                if (!tp || !tp.position) continue;
                const dx = tp.position.x - pose.position.x;
                const dz = tp.position.z - pose.position.z;
                const dd = Math.sqrt(dx * dx + dz * dz);
                if (dd < d) d = dd;
              }
              if (d > 1.0) continue;
            } else {
              const radius = h.autoVoiceoverRadius && h.autoVoiceoverRadius > 0
                ? h.autoVoiceoverRadius : 3;
              const dx = h.position.x - pose.position.x;
              const dz = h.position.z - pose.position.z;
              d = Math.sqrt(dx * dx + dz * dz);
              if (d > radius) continue;
            }
            if (d < bestDist) {
              bestDist = d;
              bestId = h.id;
            }
          }
          if (bestId) {
            forceWindowTop.targetHintId = bestId;
          } else {
            // No matching POI in range — drop the flag so we don't keep
            // probing every frame for 3 seconds.
            forceAutoVoiceoverRef.current = null;
          }
        }

        // ── POI gallery proximity scan ──────────────────────────────
        // Find the closest POI hint with a non-empty gallery within its
        // own `galleryRadius`. If a hint has `gallerySweepId` set, that
        // takes precedence over radius — the gallery activates only when
        // the visitor is on that sweep. Suppressed entirely during
        // guided tours. Emit `poiGalleryActive` / `poiGalleryInactive`
        // window events only when the active id flips, so LocationGallery
        // doesn't re-render every frame.
        {
          const tourActiveTop = matterportService.isTourActive?.() === true;
          const currentSweepTop = matterportService.getCurrentSweepId?.() || null;
          let galleryHint = null;
          let galleryDist = Infinity;
          if (!tourActiveTop) {
            for (const h of list) {
              if (!h || h.visible === false) continue;
              if (h.type !== 'poi') continue;
              const items = Array.isArray(h.gallery) ? h.gallery : [];
              if (items.length === 0) continue;
              // Sweep trigger wins outright — no distance comparison needed.
              if (h.gallerySweepId && currentSweepTop && h.gallerySweepId === currentSweepTop) {
                galleryHint = h;
                galleryDist = 0;
                break;
              }
              // If gallerySweepId is configured but doesn't match, skip
              // this POI entirely (don't fall back to radius).
              if (h.gallerySweepId) continue;
              const radius = h.galleryRadius && h.galleryRadius > 0 ? h.galleryRadius : 6;
              const dx = h.position.x - pose.position.x;
              const dz = h.position.z - pose.position.z;
              const d = Math.sqrt(dx * dx + dz * dz);
              if (d <= radius && d < galleryDist) {
                galleryDist = d;
                galleryHint = h;
              }
            }
          }
          const newId = galleryHint ? galleryHint.id : null;
          if (newId !== activeGalleryHintIdRef.current) {
            activeGalleryHintIdRef.current = newId;
            if (newId) {
              window.dispatchEvent(new CustomEvent('poiGalleryActive', {
                detail: {
                  hintId: galleryHint.id,
                  labels: galleryHint.labels || {},
                  gallery: galleryHint.gallery || [],
                },
              }));
            } else {
              window.dispatchEvent(new CustomEvent('poiGalleryInactive'));
            }
          }
        }


        if (list.length && !window.__navHintCustomLogged) {
          window.__navHintCustomLogged = true;
          // eslint-disable-next-line no-console
          console.log('🧭 NavigationHints: rendering', list.length, 'custom hint(s). First:', list[0]);
        }

        // Per-tick guard: only one POI can claim auto-voiceover playback
        // during a single pose update. Prevents overlapping/rapid-switching
        // when multiple POIs' trigger zones intersect (e.g. clustered POIs
        // around "Shri Mangal Bhagwan"). The first matching POI in iteration
        // order wins; others are skipped this tick and re-evaluated next pose.
        let autoVOClaimedThisTick = false;
        // Direction signposts are 3D meshes managed by their own
        // service. Apply the global proximity gate once per pose so
        // distant signs vanish (and faded signs ramp in) without
        // having to bake the logic into the per-hint DOM loop below.
        try { navDirectionSignService.applyProximityVisibility(pose); } catch (_) {}
        for (const h of list) {
          if (!h || h.visible === false) continue;
          // Billboards are rendered as 3D meshes by navBillboardService —
          // they intentionally do NOT participate in the DOM overlay
          // pipeline (which always orients toward camera).
          if (h.type === 'billboard') continue;
          // Direction signposts are also rendered as 3D meshes
          // (navDirectionSignService) and skip the DOM overlay pipeline.
          if (h.type === 'directionSign') continue;
          seen.add(h.id);

          // ── Ground arrow ───────────────────────────────────────────
          // Chevron strip painted on the floor at the authored world
          // point. Each chevron's 6 vertices are computed in world space
          // (along forward + side vectors derived from `forwardYaw`),
          // then projected through worldToScreen. The result is a true
          // floor decal: it foreshortens with camera pitch and stays
          // glued to its world position regardless of how the camera
          // rotates. The label is drawn separately, screen-upright,
          // anchored to the strip's far end.
          if (h.type === 'groundArrow') {
            // Pull shared visual style from the global block. Per-hint
            // values are honoured as a fallback for older configs that
            // don't yet have a globalStyle entry, but the editor only
            // ever writes the shared fields globally. Position-dependent
            // fields (forwardYaw, turnAngle, arrowLength, labels,
            // labelHeight) remain per-hint.
            const ga = navHintsService.getGlobalStyle('groundArrow');
            const offY = (h.offsetY != null) ? h.offsetY : 0.02;
            const anchorWorld = {
              x: h.position.x,
              y: h.position.y + offY,
              z: h.position.z,
            };
            const maxD = (h.maxDistance && h.maxDistance > 0) ? h.maxDistance : 12;
            const dxg = anchorWorld.x - pose.position.x;
            const dzg = anchorWorld.z - pose.position.z;
            const distG = Math.sqrt(dxg * dxg + dzg * dzg);
            let entryG = nodes.get(h.id);
            if (distG > maxD) {
              if (entryG) entryG.root.style.opacity = '0';
              continue;
            }
            // Forward + side unit vectors on the floor plane (Matterport
            // yaw: 0 faces -Z, +CW from above).
            const yaw = ((h.forwardYaw ?? 0)) * (Math.PI / 180);
            const fwx = Math.sin(yaw);
            const fwz = -Math.cos(yaw);
            const sdx = -fwz; // perpendicular CCW (= "left" of forward)
            const sdz = fwx;
            const L = (h.arrowLength ?? 2.5);
            const W = (ga.arrowWidth ?? h.arrowWidth ?? 0.9);

            // Visibility gate (replaces the old camera-approach dot).
            //
            // Project the visitor's position onto the arrow's forward
            // axis (origin = anchor / tail, forward = +L direction).
            // Regimes:
            //   sFwd < 0           → visitor is BEHIND the tail
            //                        (approaching the arrow). SHOW.
            //   0 ≤ sFwd ≤ L       → visitor is STANDING on top of
            //                        the strip. HIDE — the arrow has
            //                        served its purpose, no need to
            //                        clutter the view at their feet.
            //   sFwd > L + grace   → visitor has CROSSED past the head.
            //                        HIDE — they no longer need it.
            //
            // We additionally tolerate a moderate sideways offset
            // (`crossGrace`): if the visitor is far off to one side of
            // the strip we don't bother showing it (different path).
            const px = pose.position.x - anchorWorld.x;
            const pz = pose.position.z - anchorWorld.z;
            const sFwd = px * fwx + pz * fwz;
            const sSide = Math.abs(px * sdx + pz * sdz);
            const passGrace = (h.passGrace != null) ? h.passGrace : 0.6;
            const crossGrace = (h.crossGrace != null) ? h.crossGrace : Math.max(W * 1.5, 1.5);
            // "On top of strip" = projects within [0..L] forward and
            // within roughly half the strip width sideways (with a small
            // margin so the arrow doesn't flicker right at the edges).
            const onStripSide = (W * 0.5) + 0.3;
            const onStrip = sFwd >= 0 && sFwd <= L && sSide <= onStripSide;
            // While the author is editing this arrow, bypass the
            // visibility gate entirely. Otherwise rotating the heading
            // (or moving the position) can flip the visitor from
            // "behind the tail" → "on/past the strip" and the arrow
            // vanishes mid-edit, making it impossible to see the
            // result of the change.
            const isBeingEdited = editorSelectedIdRef.current === h.id;
            if (
              !isBeingEdited && (
                onStrip
                || sFwd > L + passGrace
                || sSide > crossGrace + Math.max(0, sFwd) * 0.15
              )
            ) {
              if (entryG) entryG.root.style.opacity = '0';
              continue;
            }
            // Curved-strip support. `turnAngle` is total bend across the
            // strip in degrees. Positive = curve to the right (CW from
            // above, matching Matterport yaw convention); negative =
            // curve to the left. 0 = straight (default).
            const theta = ((h.turnAngle ?? 0)) * (Math.PI / 180);
            const isCurved = Math.abs(theta) > 1e-4;

            // Project a world point to screen. The strip extends FORWARD
            // from the anchor point along `forwardYaw`: u=0 = tail (at
            // anchor / click point), u=1 = head (arrow tip, one length
            // away in the forward direction). Authors place the click
            // where they want the arrow to *start*, with the head
            // pointing along forwardYaw.
            //
            // When `turnAngle` is non-zero the centerline follows a
            // circular arc so the strip can act as a "turn left/right"
            // signage cue. The width offset stays perpendicular to the
            // local tangent, so the strip keeps a uniform width along
            // the curve.
            const projW = (u, v) => {
              let cf, cl, sU, cU;
              if (isCurved) {
                const a = theta * u;
                sU = Math.sin(a);
                cU = Math.cos(a);
                // Centerline in (forward, left) frame. A positive theta
                // (CW = right turn) bends toward the right, i.e. into
                // the −left direction, hence the negative `cl`.
                cf = (L * sU) / theta;
                cl = -(L * (1 - cU)) / theta;
              } else {
                sU = 0; cU = 1;
                cf = u * L;
                cl = 0;
              }
              // Perpendicular to the local tangent, pointing "left of
              // tangent" so v=0 is the right edge and v=1 is the left
              // edge (matches the straight-strip convention).
              const wf = sU * (v - 0.5) * W;
              const wl = cU * (v - 0.5) * W;
              const totalF = cf + wf;
              const totalL = cl + wl;
              const wp = {
                x: anchorWorld.x + fwx * totalF + sdx * totalL,
                y: anchorWorld.y,
                z: anchorWorld.z + fwz * totalF + sdz * totalL,
              };
              return matterportService.worldToScreen(wp, pose);
            };

            // Anchor must project somewhere on screen — but we no longer
            // require all four strip corners to be in front of the camera.
            // When the visitor walks on top of the arrow and looks down,
            // some corners can clip behind the near plane while the
            // anchor (and most chevrons) stay visible. Per-chevron
            // visibility is decided in the geometry loop below: any
            // chevron whose 6 vertices are all valid is drawn; chevrons
            // with any clipped vertex are temporarily collapsed (zero
            // points) instead of taking down the whole strip.
            const anchorProj = matterportService.worldToScreen(anchorWorld, pose);
            if (!anchorProj) {
              if (entryG) entryG.root.style.opacity = '0';
              continue;
            }

            // Variant-driven shape config.
            const variant = (ga.arrowVariant || h.arrowVariant || 'chevrons');
            const VARIANTS = {
              chevrons:      { count: 7, body: 0.50, tip: 0.25, back: 0.20, hasV: true,  shape: 'chevron' },
              triangles:     { count: 6, body: 0.55, tip: 0.30, back: 0.00, hasV: false, shape: 'triangle' },
              doubleChevron: { count: 4, body: 0.55, tip: 0.30, back: 0.20, hasV: true,  shape: 'chevron' },
              dashes:        { count: 9, body: 0.65, tip: 0.00, back: 0.00, hasV: false, shape: 'dash' },
              beam:          { count: 1, body: 0.95, tip: 0.20, back: 0.00, hasV: true,  shape: 'beam' },
            };
            const cfg = VARIANTS[variant] || VARIANTS.chevrons;
            // Optional override of the chevron count from the global
            // style. 0 (or unset) keeps the variant's natural count;
            // any positive integer uses that many breaks across the
            // strip (capped at 1..30 to keep DOM growth sane). The
            // `beam` variant is a single solid arrow so we never split
            // it; it ignores chevronCount.
            const ccOverride = (ga.chevronCount ?? 0) | 0;
            const N = cfg.shape === 'beam'
              ? cfg.count
              : (ccOverride > 0 ? Math.max(1, Math.min(30, ccOverride)) : cfg.count);
            const tailOpacity = Math.max(0, Math.min(1, (ga.arrowTailOpacity ?? h.arrowTailOpacity ?? 0.05)));

            // Build node lazily, sized for the current variant.
            // Rebuild also when the chevron count changes (global
            // chevronCount override) — the polygon pool size is fixed
            // at build time so we need a fresh DOM tree.
            const needRebuild = !entryG || entryG._variant !== variant || entryG._N !== N;
            if (entryG && needRebuild) {
              try { entryG.root.remove(); } catch (_) {}
              nodes.delete(h.id);
              entryG = null;
            }
            if (!entryG) {
              const root = document.createElement('div');
              root.className = 'nav-hint-ground-arrow';
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('class', 'nha-svg');
              svg.style.position = 'fixed';
              svg.style.left = '0';
              svg.style.top = '0';
              svg.style.width = '100vw';
              svg.style.height = '100vh';
              svg.style.pointerEvents = 'none';
              svg.style.overflow = 'visible';
              const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
              const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
              grad.setAttribute('id', 'nha-grad-' + h.id);
              grad.setAttribute('gradientUnits', 'userSpaceOnUse');
              const s1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              s1.setAttribute('offset', '0'); s1.setAttribute('stop-color', 'currentColor');
              const s2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
              s2.setAttribute('offset', '1'); s2.setAttribute('stop-color', 'currentColor'); s2.setAttribute('stop-opacity', '1');
              grad.appendChild(s1); grad.appendChild(s2);
              defs.appendChild(grad);
              svg.appendChild(defs);
              const polys = [];
              for (let i = 0; i < N; i += 1) {
                const p = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
                p.setAttribute('fill', 'url(#nha-grad-' + h.id + ')');
                svg.appendChild(p);
                polys.push(p);
              }
              root.appendChild(svg);
              // Upright billboard label: a DOM <span> that always faces
              // the camera (screen-aligned), positioned at the
              // world-projected head of the arrow. White text, no
              // background pill — pure floating letters.
              const label = document.createElement('span');
              label.className = 'nav-hint-ground-arrow-label';
              root.appendChild(label);
              host.appendChild(root);
              entryG = { root, svg, grad, polys, label, type: 'groundArrow', _variant: variant, _N: N };
              nodes.set(h.id, entryG);
            }

            entryG.root.style.color = (ga.arrowColor || h.arrowColor || '#f59e0b');
            // Tail opacity ramp: tail (u=0) → head (u=1).
            entryG.grad.firstChild.setAttribute('stop-opacity', tailOpacity.toFixed(2));

            // Update gradient direction so the head end is darkest.
            const gA = projW(0, 0.5);
            const gB = projW(1, 0.5);
            if (gA && gB && entryG.grad) {
              entryG.grad.setAttribute('x1', gA.x.toFixed(1));
              entryG.grad.setAttribute('y1', gA.y.toFixed(1));
              entryG.grad.setAttribute('x2', gB.x.toFixed(1));
              entryG.grad.setAttribute('y2', gB.y.toFixed(1));
            }

            // Lay out the segments along u ∈ [0..1]. Each segment has a
            // body of `body * stride` and (optionally) a tip extension
            // of `tip * stride`. The remaining stride is the gap.
            // When a single chevron has any vertex clipped behind the
            // camera (e.g. visitor standing on top of the strip), we
            // collapse just that chevron (empty points) and keep the
            // rest visible — the strip never disappears entirely.
            //
            // Tail-to-head flow animation: a bright pulse travels from
            // the tail (i=0) to the head (i=N-1) along the strip. The
            // pulse is a Gaussian centred at `phase` (a normalised
            // position 0..1 advancing at `arrowFlowSpeed` cycles/sec).
            // Each chevron's `opacity` attr is set to floor +
            // (1-floor)*gauss; the underlying linearGradient continues
            // to colour the polygons, so the flow rides on top of the
            // static tail→head fade.
            const flowEnabled = (ga.arrowFlow !== undefined ? ga.arrowFlow : (h.arrowFlow !== false));
            const flowSpeed = (ga.arrowFlowSpeed ?? h.arrowFlowSpeed ?? 0.25);
            const flowSigma = Math.max(0.04, (ga.arrowFlowWidth ?? h.arrowFlowWidth ?? 0.35));
            const flowFloor = Math.max(0, Math.min(1, (ga.arrowFlowFloor ?? h.arrowFlowFloor ?? 0.8)));
            // Highlight mode (visitor close): a touch faster and a
            // slightly lower floor so the pulse becomes a bit more
            // visible — but still subtle, not a strobe.
            const inHL = distG <= (ga.highlightRadius ?? h.highlightRadius ?? 3);
            const speed = flowSpeed * (inHL ? 1.25 : 1.0);
            const floor = inHL ? Math.max(0, flowFloor - 0.1) : flowFloor;
            const phase = flowEnabled
              ? ((performance.now() / 1000) * speed) % 1
              : 0;
            const stride = 1 / N;
            let anyDrawn = false;
            for (let i = 0; i < N; i += 1) {
              const u0 = i * stride;
              const u1 = u0 + cfg.body * stride;
              const uMid = u1 + cfg.tip * stride;
              const uBack = u0 + cfg.back * stride;
              let pts;
              if (cfg.shape === 'chevron') {
                pts = [
                  projW(u0, 0),
                  projW(u1, 0),
                  projW(uMid, 0.5),
                  projW(u1, 1),
                  projW(u0, 1),
                  projW(uBack, 0.5),
                ];
              } else if (cfg.shape === 'triangle') {
                pts = [
                  projW(u0, 0),
                  projW(u1, 0),
                  projW(uMid, 0.5),
                  projW(u1, 1),
                  projW(u0, 1),
                ];
              } else if (cfg.shape === 'dash') {
                pts = [
                  projW(u0, 0.25),
                  projW(u1, 0.25),
                  projW(u1, 0.75),
                  projW(u0, 0.75),
                ];
              } else if (cfg.shape === 'beam') {
                pts = [
                  projW(0, 0.4),
                  projW(0.7, 0.4),
                  projW(0.7, 0.15),
                  projW(1.0, 0.5),
                  projW(0.7, 0.85),
                  projW(0.7, 0.6),
                  projW(0, 0.6),
                ];
              } else {
                pts = [
                  projW(u0, 0),
                  projW(u1, 0),
                  projW(uMid, 0.5),
                  projW(u1, 1),
                  projW(u0, 1),
                ];
              }
              // Per-chevron near-plane safety: drop a chevron whenever
              // any of its vertices is closer than ~0.4 m from the
              // camera. The SDK's `inFront` flag only checks z > 0,
              // which lets vertices that are millimetres in front of
              // the near plane through — those project to huge screen
              // coordinates and cause "yellow glitter" streaks across
              // the top of the screen as the visitor walks over the
              // strip. We also add a screen-space sanity gate: any
              // vertex projected far outside the viewport (or to
              // non-finite coordinates) is treated as a
              // perspective-blowup artefact and the polygon is
              // suppressed for that frame.
              const NEAR_CLIP = 0.4;
              const vw = window.innerWidth || 1920;
              const vh = window.innerHeight || 1080;
              const SCREEN_LIMIT = 6; // 6× viewport size = clearly bogus
              const maxAbsX = vw * SCREEN_LIMIT;
              const maxAbsY = vh * SCREEN_LIMIT;
              if (
                !pts
                || pts.some(
                  (p) =>
                    !p
                    || !p.inFront
                    || (typeof p.z === 'number' && p.z < NEAR_CLIP)
                    || !Number.isFinite(p.x)
                    || !Number.isFinite(p.y)
                    || Math.abs(p.x) > maxAbsX
                    || Math.abs(p.y) > maxAbsY,
                )
              ) {
                // Drop this chevron only — keep the rest of the strip.
                entryG.polys[i].setAttribute('points', '');
                continue;
              }
              entryG.polys[i].setAttribute(
                'points',
                pts.map((p) => p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' ')
              );
              // Per-chevron flow opacity: Gaussian pulse centred at
              // `phase`, with cyclic distance so the wave wraps back
              // smoothly to the tail. When flow is disabled every
              // chevron just renders fully opaque (and the static
              // gradient still provides the tail→head fade).
              if (flowEnabled && N > 1) {
                const localPos = i / (N - 1);
                let d = localPos - phase;
                if (d < -0.5) d += 1;
                if (d > 0.5) d -= 1;
                const pulse = Math.exp(-(d * d) / (2 * flowSigma * flowSigma));
                const op = floor + (1 - floor) * pulse;
                entryG.polys[i].setAttribute('opacity', op.toFixed(3));
              } else {
                entryG.polys[i].setAttribute('opacity', '1');
              }
              anyDrawn = true;
            }
            if (!anyDrawn) {
              entryG.root.style.opacity = '0';
              continue;
            }

            // Floating label: anchored to a fixed world point at the
            // arrow tip, but always billboarded toward the camera so
            // it stays readable from any approach angle. The world
            // anchor doesn't move, so the label stays put in 3D — only
            // its 2D screen position updates as the camera moves.
            // Curved arrows follow the arc to the actual tip.
            const lang = language;
            // Resolve labels: when a Target POI is linked and the
            // author hasn't ticked "Custom labels", mirror the POI's
            // labels. Otherwise use the arrow's own labels.
            let gaLabels = h.labels;
            if (h.targetId && h.customLabels !== true) {
              const linkedPoi = list.find((x) => x && x.id === h.targetId && x.type === 'poi');
              if (linkedPoi && linkedPoi.labels) gaLabels = linkedPoi.labels;
            }
            const labelText = (gaLabels && (gaLabels[lang] || gaLabels.en || gaLabels.mr || gaLabels.hi)) || '';
            // Per-hint line mode: 'auto' (default — no wrap), '1'
            // (force single line, identical to auto for ground arrow),
            // or '2' (force two lines: split on a literal \n if the
            // author put one in, otherwise break at the middle-most
            // whitespace).
            const lineMode = h.labelLineMode || 'auto';
            let renderedLabel = labelText;
            if (lineMode === '2' && labelText) {
              if (labelText.indexOf('\n') >= 0) {
                renderedLabel = labelText;
              } else {
                const trimmed = labelText.trim();
                const tokens = trimmed.split(/\s+/);
                if (tokens.length >= 2) {
                  // Find the split point closest to the middle by
                  // character count so the two lines look balanced.
                  const total = trimmed.length;
                  let bestIdx = 1;
                  let bestDiff = Infinity;
                  let cum = 0;
                  for (let i = 0; i < tokens.length - 1; i += 1) {
                    cum += tokens[i].length + 1; // +1 for the space
                    const diff = Math.abs(cum - total / 2);
                    if (diff < bestDiff) { bestDiff = diff; bestIdx = i + 1; }
                  }
                  renderedLabel =
                    tokens.slice(0, bestIdx).join(' ') + '\n' +
                    tokens.slice(bestIdx).join(' ');
                }
              }
            }
            if (entryG.label.textContent !== renderedLabel) entryG.label.textContent = renderedLabel;
            const wantTwoLine = lineMode === '2' && renderedLabel.indexOf('\n') >= 0;
            if (entryG._twoLine !== wantTwoLine) {
              entryG._twoLine = wantTwoLine;
              entryG.label.classList.toggle('is-two-line', wantTwoLine);
            }
            if (!labelText) {
              entryG.label.style.display = 'none';
            } else {
              entryG.label.style.display = '';
              // Vertical offset above the arrow head. Comes from the
              // global ground-arrow style so the whole tour shares one
              // value. Per-hint `labelHeight` is still honoured as a
              // fallback for older configs.
              const labelH = (ga.labelHeight ?? h.labelHeight ?? 0.6);
              // Place the label slightly past the head end of the
              // strip. For curved strips we follow the arc so the
              // label ends up over the actual arrow tip rather than
              // out in space along the original forward direction.
              const uHead = 1.05;
              let headF, headL;
              if (isCurved) {
                const a = theta * uHead;
                headF = (L * Math.sin(a)) / theta;
                headL = -(L * (1 - Math.cos(a))) / theta;
              } else {
                headF = uHead * L;
                headL = 0;
              }
              const headWorld = {
                x: anchorWorld.x + fwx * headF + sdx * headL,
                y: anchorWorld.y + labelH,
                z: anchorWorld.z + fwz * headF + sdz * headL,
              };
              // World-space size for the label, clamped to the base
              // font px so it shrinks with distance like real signage
              // but NEVER grows larger than the author's chosen size
              // when the visitor stands right next to the arrow. We
              // compute the on-screen height of a
              // `labelWorldScale * labelFontSize` segment by projecting
              // two world points one font-size apart in world Y, then
              // clamp to [minPx, basePx].
              const worldPerPixel = (ga.labelWorldScale ?? h.labelWorldScale ?? 0.01);
              const baseFontPx = (ga.labelFontSize ?? h.labelFontSize ?? 16) | 0;
              const worldHeight = baseFontPx * worldPerPixel;
              const headProj = matterportService.worldToScreen(headWorld, pose);
              const topProj = matterportService.worldToScreen(
                { x: headWorld.x, y: headWorld.y + worldHeight, z: headWorld.z },
                pose,
              );
              if (headProj && headProj.inFront && topProj && topProj.inFront) {
                const rawPx = Math.abs(topProj.y - headProj.y);
                // Clamp upper bound to baseFontPx so close-up labels
                // don't balloon. Lower bound keeps far-away labels
                // legible. Authors raise/lower the cap by adjusting
                // "Label font size" in the global settings.
                const screenPx = Math.max(8, Math.min(baseFontPx, rawPx));
                entryG.label.style.opacity = '1';
                entryG.label.style.fontSize = screenPx.toFixed(1) + 'px';
                // Always billboard: just translate to the projected
                // anchor and centre the label horizontally above it.
                // No rotation, no foreshortening — the label always
                // faces the camera and stays readable, while its
                // world anchor keeps it pinned in 3D space.
                entryG.label.style.transformOrigin = '50% 100%';
                entryG.label.style.transform =
                  'translate(' + headProj.x.toFixed(1) + 'px,' + headProj.y.toFixed(1)
                  + 'px) translate(-50%,-100%)';
              } else {
                entryG.label.style.opacity = '0';
              }
            }

            // Linear fade on approach to maxDistance.
            const fadeS = h.fadeStart && h.fadeStart > 0 ? h.fadeStart : 0;
            let alphaG = 1;
            if (fadeS > 0 && fadeS < maxD) {
              alphaG = distG <= fadeS ? 1
                : distG >= maxD ? 0
                : 1 - (distG - fadeS) / (maxD - fadeS);
            }
            entryG.root.style.opacity = alphaG.toFixed(2);
            // Highlight pulse when within highlightRadius.
            const hr = (ga.highlightRadius ?? h.highlightRadius ?? 3);
            const wantHL = distG <= hr;
            if (entryG._hl !== wantHL) {
              entryG._hl = wantHL;
              entryG.root.classList.toggle('nav-hint-ground-arrow--hl', wantHL);
            }
            continue;
          }

          // Resolve per-type global style once per hint. For POI and
          // pill types, global *wins* — per-hint visual fields are
          // ignored so one setting change updates every existing hint.
          // Nav arrows (legacy) still read their own fields.
          const hintType = h.type === 'pill' ? 'pill'
            : h.type === 'poi' ? 'poi' : 'nav';
          const gStyle = (hintType === 'poi' || hintType === 'pill')
            ? navHintsService.getGlobalStyle(hintType)
            : null;
          const style = gStyle
            ? {
              shape: gStyle.shape,
              size: gStyle.size,
              fontSize: gStyle.fontSize,
              textColor: gStyle.textColor,
              bgColor: gStyle.bgColor,
              bgOpacity: gStyle.bgOpacity,
              fontWeight: gStyle.fontWeight,
              // Vertical height is per-hint (each POI / pill can sit at a
              // different elevation) — it is not part of the global style.
              offsetY: h.offsetY || 0,
              iconScale: gStyle.iconScale,
            }
            : {
              shape: h.shape,
              size: h.size,
              fontSize: h.fontSize,
              textColor: h.textColor,
              bgColor: h.bgColor,
              bgOpacity: h.bgOpacity,
              fontWeight: h.fontWeight,
              offsetY: h.offsetY || 0,
              iconScale: h.iconScale,
            };

          const world = {
            x: h.position.x,
            y: h.position.y + (style.offsetY || 0),
            z: h.position.z,
          };

          // ── Proximity gate ────────────────────────────────────────────
          const maxDist = h.maxDistance && h.maxDistance > 0 ? h.maxDistance : 8;
          const dxFloor = world.x - pose.position.x;
          const dzFloor = world.z - pose.position.z;
          const distFloor = Math.sqrt(dxFloor * dxFloor + dzFloor * dzFloor);

          // ── Proximity audio (independent of visual gates) ─────────────
          // Plays whenever the viewer enters the hint's audioRadius sphere,
          // pauses on exit. Runs before any `continue` below so audio still
          // works when the marker itself is off-screen.
          if (h.audioSrc) {
            const audioMap = audioNodesRef.current;
            let aEntry = audioMap.get(h.id);
            if (!aEntry || aEntry.src !== h.audioSrc) {
              // Source changed (or first time) — tear down any old element.
              if (aEntry) {
                try { aEntry.el.pause(); aEntry.el.src = ''; aEntry.el.remove(); } catch (_) {}
              }
              const el = document.createElement('audio');
              el.src = h.audioSrc;
              el.preload = 'auto';
              el.style.display = 'none';
              document.body.appendChild(el);
              aEntry = { el, src: h.audioSrc, playing: false };
              audioMap.set(h.id, aEntry);
            }
            aEntry.el.loop = h.audioLoop !== false;
            aEntry.el.volume = Math.max(0, Math.min(1, h.audioVolume ?? 0.8));
            const aRadius = h.audioRadius && h.audioRadius > 0 ? h.audioRadius : 4;
            const inRange = distFloor <= aRadius;
            // Honour the global mute — proximity audio bypasses
            // audioService's masterGain so we must gate it here too.
            const globallyMuted = !!audioService.isMuted?.();
            if (inRange && !aEntry.playing && !globallyMuted) {
              const p = aEntry.el.play();
              if (p && typeof p.catch === 'function') {
                // Autoplay can be blocked until a user gesture; swallow error.
                p.catch(() => {});
              }
              aEntry.playing = true;
            } else if ((!inRange || globallyMuted) && aEntry.playing) {
              try { aEntry.el.pause(); } catch (_) {}
              aEntry.playing = false;
            }
          }

          // ── Auto-play voiceover on approach (POI only) ────────────────
          // Trigger when the camera is BOTH within autoVoiceoverRadius
          // metres of the POI AND looking toward it inside the
          // autoVoiceoverFacingArc cone. Stop when either condition
          // breaks; voiceoverService captures the playback offset so a
          // return within ~60 s resumes from where it left off.
          //
          // Additional gates (manual-navigation only):
          //   • Suppressed entirely while a guided tour is running — the
          //     tour has its own per-stop narration pipeline.
          //   • Suppressed while a floor/footer quick-nav fly is in
          //     progress — narration is meant for organic exploration,
          //     not jump-cuts.
          //   • Requires the camera to have actually settled at a sweep
          //     (≥250 ms since the last Sweep.current change) so it
          //     doesn't fire while flying past the POI in transit.
          if (h.type === 'poi' && h.autoVoiceover && h.voiceoverTagId) {
            // Tolerances for the new trigger-points model.
            const TP_RADIUS_M       = 0.6;   // must be within 0.6 m of a captured point
            const TP_YAW_BUFFER_DEG = 30;    // ±30° of captured camera yaw (60° cone)
            const TP_HOLD_RADIUS_M  = 1.2;   // hysteresis hold radius
            const TP_HOLD_YAW_DEG   = 55;    // hysteresis hold yaw window

            const triggerPoints = Array.isArray(h.triggerPoints) ? h.triggerPoints : [];
            const useTriggerPoints = triggerPoints.length > 0;

            // Camera position + yaw needed for both modes.
            const camYawDeg = pose.rotation?.y || 0;
            const yawDelta = (target) => {
              let d = ((camYawDeg - target + 540) % 360) - 180;
              return Math.abs(d);
            };
            const distTo = (p) => {
              const dx = p.x - pose.position.x;
              const dz = p.z - pose.position.z;
              return Math.sqrt(dx * dx + dz * dz);
            };

            // Compute inWindow / inHoldWindow + a representative
            // distance for diagnostic logging.
            let inWindow = false;
            let inHoldWindow = false;
            let triggerDist = distFloor;

            if (useTriggerPoints) {
              let bestDist = Infinity;
              for (const tp of triggerPoints) {
                if (!tp || !tp.position) continue;
                const d = distTo(tp.position);
                const yd = yawDelta(tp.cameraYawDeg || 0);
                if (d <= TP_RADIUS_M && yd <= TP_YAW_BUFFER_DEG) {
                  inWindow = true;
                }
                if (d <= TP_HOLD_RADIUS_M && yd <= TP_HOLD_YAW_DEG) {
                  inHoldWindow = true;
                }
                if (d < bestDist) bestDist = d;
              }
              if (bestDist !== Infinity) triggerDist = bestDist;
            }
            // POIs without explicit triggerPoints are NOT auto-played
            // during manual walking — this prevents multiple nearby
            // POIs from triggering simultaneously and overlapping audio
            // (e.g. around "Shri Mangal Bhagwan"). Such POIs can still
            // be played by clicking the speaker icon on the label, by
            // tag click, or via the footer quick-nav (force window).

            // Force-trigger window (set by 'quickNavArrival' event from
            // LocationQuickNav). When active, treat the closest matching
            // POI within trigger range as if the user had arrived
            // organically: bypass facing/yaw and alreadyFinished gates,
            // and skip the sweep-settled wait.
            const forceWindow = forceAutoVoiceoverRef.current;
            const forceActive = !!forceWindow
              && performance.now() < forceWindow.expiresAt
              && forceWindow.targetHintId === h.id;

            const EXIT_GRACE_MS = 1200;
            const ownsPlayback = autoVoiceoverHintIdRef.current === h.id;
            const alreadyFinished = autoVoiceoverFinishedRef.current.has(h.id);

            // Manual-only + sweep-arrival gate. Computed once per hint.
            const tourActive = matterportService.isTourActive?.() === true;
            const quickNavFlying = matterportService.isQuickNavFlying?.() === true;
            const sinceArrival = matterportService.msSinceLastSweepArrival?.() ?? Infinity;
            const sweepSettled = sinceArrival >= 250;
            // If a footer-initiated standalone tour narration is currently
            // playing, don't fire a proximity POI auto-VO on top of it —
            // they'd cross-fade for ~0.4 s and sound like the start
            // doubles. Let the standalone finish; user can manually tap
            // the speaker on the POI label if they want the tag VO too.
            const tourNarrationPlaying = !!audioService.getCurrentNarrationKey?.();
            const allowTrigger = !tourActive && !quickNavFlying && sweepSettled && !tourNarrationPlaying;

            if ((inWindow && allowTrigger && !ownsPlayback && !alreadyFinished && !autoVOClaimedThisTick)
                || (forceActive && !tourActive && !quickNavFlying && !ownsPlayback && !autoVOClaimedThisTick)) {
              // Take ownership: stop whatever else is playing (which
              // captures *its* resume offset under that tag id) and
              // start this POI's narration. Skip if some other UI is
              // already playing this exact tag — let it finish.
              const lang = languageRef.current || 'mr';
              // eslint-disable-next-line no-console
              console.log(`🎯 Auto-VO trigger: "${h.labels?.en || h.id}" tag=${h.voiceoverTagId} lang=${lang} mode=${useTriggerPoints ? 'triggerPoints' : 'legacy'} dist=${triggerDist.toFixed(2)}m force=${!!forceActive}`);
              const otherTagPlaying = voiceoverService.isPlaying()
                && voiceoverService._currentTagId
                && voiceoverService._currentTagId !== h.voiceoverTagId;
              if (otherTagPlaying) voiceoverService.stop();
              if (forceActive) {
                autoVoiceoverFinishedRef.current.delete(h.id);
                forceAutoVoiceoverRef.current = null;
              }
              if (!voiceoverService.isPlaying()
                  || voiceoverService._currentTagId !== h.voiceoverTagId) {
                voiceoverService.playForTag(h.voiceoverTagId, lang, {
                  onEnded: () => {
                    if (autoVoiceoverHintIdRef.current === h.id) {
                      autoVoiceoverHintIdRef.current = null;
                    }
                    autoVoiceoverExitAtRef.current = 0;
                    autoVoiceoverFinishedRef.current.add(h.id);
                  },
                });
              }
              autoVoiceoverHintIdRef.current = h.id;
              autoVoiceoverExitAtRef.current = 0;
              autoVOClaimedThisTick = true;
            } else if (ownsPlayback && (tourActive || quickNavFlying)) {
              voiceoverService.stop();
              autoVoiceoverHintIdRef.current = null;
              autoVoiceoverExitAtRef.current = 0;
            } else if (ownsPlayback && !inHoldWindow) {
              const now = performance.now();
              if (autoVoiceoverExitAtRef.current === 0) {
                autoVoiceoverExitAtRef.current = now;
              } else if (now - autoVoiceoverExitAtRef.current >= EXIT_GRACE_MS) {
                voiceoverService.fadeOut(2.5);
                autoVoiceoverHintIdRef.current = null;
                autoVoiceoverExitAtRef.current = 0;
              }
            } else if (ownsPlayback && inHoldWindow) {
              autoVOClaimedThisTick = true;
              autoVoiceoverExitAtRef.current = 0;
            }
            if (!inHoldWindow && alreadyFinished) {
              autoVoiceoverFinishedRef.current.delete(h.id);
            }
          }

          let visibilityAlpha = 1;
          if (distFloor > maxDist) {
            const entryStale = nodes.get(h.id);
            if (entryStale) entryStale.root.style.opacity = '0';
            continue;
          }

          // ── Pill arrival gate ─────────────────────────────────────────
          // For wayfinding pills only: hide once the viewer has reached
          // the pill's anchor (within `hideWithin` metres on the floor
          // plane). Stops the pill nagging "go right" when the user is
          // already standing on it.
          if (h.type === 'pill') {
            const hideRadius = h.hideWithin && h.hideWithin > 0 ? h.hideWithin : 0;
            if (hideRadius > 0 && distFloor <= hideRadius) {
              const entryStale = nodes.get(h.id);
              if (entryStale) entryStale.root.style.opacity = '0';
              continue;
            }
          }

          // Fade in as the user nears (linear between maxDist → fadeStart).
          const fadeStart = h.fadeStart && h.fadeStart > 0 ? h.fadeStart : 0;
          if (fadeStart > 0 && fadeStart < maxDist && distFloor > fadeStart) {
            visibilityAlpha = 1 - (distFloor - fadeStart) / (maxDist - fadeStart);
          }

          // ── Direction gate ────────────────────────────────────────────
          // Only show if the user is approaching from the authored side.
          // approachVector is the normalized (hintPos - cameraPos) at author
          // time (floor plane). We compare it with the current user→hint
          // direction via dot product.
          // POI markers + wayfinding pills are always omnidirectional —
          // skip the gate entirely.
          const isPoi = h.type === 'poi';
          const isPill = h.type === 'pill';
          // POI markers are always omni-visible. Nav arrows and pills
          // honour an optional approachVector — if authored, the hint is
          // only shown when the camera is approaching from that side.
          if (!isPoi && h.approachVector && (h.approachVector.x !== 0 || h.approachVector.z !== 0)) {
            const len = Math.max(distFloor, 0.0001);
            const ux = dxFloor / len;
            const uz = dzFloor / len;
            const dot = ux * h.approachVector.x + uz * h.approachVector.z;
            const thr = typeof h.approachThreshold === 'number' ? h.approachThreshold : 0.3;
            if (dot < thr) {
              const entryStale = nodes.get(h.id);
              if (entryStale) entryStale.root.style.opacity = '0';
              continue;
            }
          }

          // ── POI visibility cone ──────────────────────────────────────
          // For POIs, an optional `viewYaw` + `viewArc` pair restricts
          // visibility to a wedge centred on the POI's "front" facing
          // direction. Hides the front-of-building label when the user
          // is walking around the back. Skipped when viewArc is at the
          // default 360° (omnidirectional) or viewYaw is unset.
          if (isPoi && h.viewYaw != null && h.viewArc != null && h.viewArc < 360) {
            const len = Math.max(distFloor, 0.0001);
            // POI → camera direction on the floor plane.
            const pcx = -dxFloor / len;
            const pcz = -dzFloor / len;
            // POI's authored "front" direction (Matterport yaw convention:
            // yaw=0 faces -Z, increases CCW from above).
            const yRad = (h.viewYaw * Math.PI) / 180;
            const fx = Math.sin(yRad);
            const fz = -Math.cos(yRad);
            const dot = pcx * fx + pcz * fz;
            // viewArc is the *total* arc width in degrees; threshold is
            // cos(arc/2). arc=180 → thr=0 (front half-space), arc=90 →
            // thr≈0.707 (narrow front cone).
            const halfArcRad = (h.viewArc * Math.PI) / 360;
            const thr = Math.cos(halfArcRad);
            if (dot < thr) {
              const entryStale = nodes.get(h.id);
              if (entryStale) entryStale.root.style.opacity = '0';
              continue;
            }
          }

          const proj = matterportService.worldToScreen(world, pose);
          let entry = nodes.get(h.id);

          if (!nodes.has(h.id) && !window[`__nhProj_${h.id}`]) {
            window[`__nhProj_${h.id}`] = true;
            // eslint-disable-next-line no-console
            console.log('🧭 hint projection', h.id, { world, proj, pose: pose.position });
          }

          if (!proj || !proj.inFront) {
            if (entry) entry.root.style.opacity = '0';
            continue;
          }

          if (!entry) {
            const root = document.createElement('div');
            root.className = 'nav-hint-custom';
            const arrow = document.createElement('span');
            arrow.className = 'nav-hint-custom-arrow';
            const label = document.createElement('span');
            label.className = 'nav-hint-custom-label';
            root.appendChild(arrow);
            root.appendChild(label);
            host.appendChild(root);
            entry = { root, label, arrow, type: null, clickHandler: null };
            nodes.set(h.id, entry);
          }

          // Toggle per-type styling & interactivity.
          //   POI  → halo ring via .nav-hint-custom--poi
          //   pill → non-interactive wayfinding aid (live compass + distance);
          //          .nav-hint-custom--pill. Pills are navigation HELPERS only —
          //          clicking them no longer moves the camera.
          //   nav  → plain directional arrow
          const nextType = isPoi ? 'poi' : isPill ? 'pill' : 'nav';
          if (entry.type !== nextType) {
            entry.type = nextType;
            entry.root.classList.toggle('nav-hint-custom--poi', isPoi);
            entry.root.classList.toggle('nav-hint-custom--pill', isPill);
            // Remove any previously-wired click handler — pills no longer
            // navigate on click, they're purely visual wayfinding aids.
            if (entry.clickHandler) {
              entry.root.removeEventListener('click', entry.clickHandler);
              entry.clickHandler = null;
            }
            if (entry.mouseDownHandler) {
              entry.root.removeEventListener('mousedown', entry.mouseDownHandler);
              entry.mouseDownHandler = null;
            }
            // Non-interactive by default for both POI and pill (pills used
            // to be clickable; that was removed). Cursor/pointer-events
            // are raised back up below when the editor is active with the
            // hint selected (for drag-to-reposition).
            entry.root.style.cursor = '';
            entry.root.style.pointerEvents = '';
            // Editor drag-to-move — attached to POI and pill nodes. The
            // handler short-circuits unless the editor is open and this
            // hint is the current selection, so live-viewer interactions
            // are unaffected.
            if (isPoi || isPill) {
              entry.mouseDownHandler = (ev) => {
                if (!editorActiveRef.current) return;
                if (editorSelectedIdRef.current !== entry._hintId) return;
                ev.preventDefault();
                ev.stopPropagation();
                beginDrag(entry._hintId, ev);
              };
              entry.root.addEventListener('mousedown', entry.mouseDownHandler);
            }
          }
          // Stash the hint id on the node so handlers (wired once on type
          // change) can read the current id even if list ordering shifts.
          entry._hintId = h.id;
          // Visual indication that this hint is the editor's current
          // selection: a glowing outline ring driven entirely from CSS.
          // Toggled on POIs and pills only; nav arrows ignore selection.
          const isSelected = !!editorActiveRef.current
            && editorSelectedIdRef.current === h.id
            && (isPoi || isPill);
          if (entry._isSelected !== isSelected) {
            entry._isSelected = isSelected;
            entry.root.classList.toggle('nav-hint-custom--selected', isSelected);
          }
          // While the editor is open with this hint selected, make the
          // node draggable and hint at it with a move cursor. Otherwise
          // POIs and pills are both pass-through (no click-to-navigate).
          if (isSelected) {
            entry.root.style.pointerEvents = 'auto';
            entry.root.style.cursor = 'move';
          } else {
            entry.root.style.pointerEvents = '';
            entry.root.style.cursor = '';
          }
          // Keep the target SID on the node for bookkeeping — pills may
          // still use it for label/compass calculations even though they
          // no longer navigate on click.
          if (isPill) entry._targetSid = h.targetSid || '';

          const lang = language;
          const labelText = (h.labels && (h.labels[lang] || h.labels.en || h.labels.mr || h.labels.hi)) || '';

          if (isPill) {
            // ── Wayfinding pill layout ─────────────────────────────────
            // Matches the "Directional Pills" mockup: orange circle icon,
            // bold destination name, thin divider, direction label below.
            // CSS owns the visuals so we skip the generic shape/inline-
            // style block below.
            //
            // Direction label is camera-relative and points at the real
            // *destination* (the linked target snapshot), not at the pill
            // itself. That way the pill can float anywhere in the scene
            // as a signpost — e.g. hanging beside a path — while the
            // label always reflects where you still need to go. If no
            // target is set we fall back to the pill's own position.
            // Matterport yaw convention: rotation.y in degrees, 0 faces
            // -Z, rotates CW viewed from above. Forward = (sin yaw,
            // -cos yaw), right = (cos yaw, sin yaw).
            let destX = world.x;
            let destY = world.y;
            let destZ = world.z;
            let destPoi = null;
            if (h.targetSid) {
              // targetSid now references a POI hint's id (authored via the
              // editor). Fall through to the pill's own position if the
              // POI was deleted or the id is stale.
              const list = customHintsRef.current || [];
              const poi = list.find((p) => p && p.id === h.targetSid);
              if (poi && poi.position) {
                destX = poi.position.x;
                destY = poi.position.y + (poi.offsetY || 0);
                destZ = poi.position.z;
                destPoi = poi;
              }
            }
            const ddx = destX - pose.position.x;
            const ddz = destZ - pose.position.z;
            const yawRad = (pose.rotation?.y || 0) * (Math.PI / 180);
            const fx = Math.sin(yawRad);
            const fz = -Math.cos(yawRad);
            const rx = Math.cos(yawRad);
            const rz = Math.sin(yawRad);
            // World-space signed angle from forward (positive = right of
            // camera). Used as the *fallback* for the label when the POI
            // is behind the camera and can't be projected.
            const worldAngleDeg = Math.atan2(
              ddx * rx + ddz * rz,
              ddx * fx + ddz * fz,
            ) * 180 / Math.PI;

            // Lazy-init the pill's structured markup once. After that we
            // just update text nodes in place — no React, no innerHTML
            // thrash per frame.
            // `PILL_MARKUP_VERSION` forces a rebuild when the DOM schema
            // changes (e.g. distance span removed). Bump whenever the
            // pill's children shape changes so stale hot-reloaded nodes
            // don't keep the old structure alive.
            const PILL_MARKUP_VERSION = 3;
            if (!entry._pillBuilt || entry._pillVersion !== PILL_MARKUP_VERSION) {
              entry.root.innerHTML = '';
              const icon = document.createElement('span');
              icon.className = 'nav-hint-pill-icon';
              // Inline SVG arrow — unicode arrow glyphs (⬆/↑/etc.) have
              // inconsistent vertical metrics across fonts, so they sit
              // visibly off-centre inside the circle badge. An SVG viewBox
              // guarantees pixel-perfect centering and crisp rendering at
              // any badge size. Direction is controlled by `arrowDeg`.
              const svgNS = 'http://www.w3.org/2000/svg';
              const svg = document.createElementNS(svgNS, 'svg');
              svg.setAttribute('viewBox', '0 0 24 24');
              svg.setAttribute('class', 'nav-hint-pill-arrow');
              svg.setAttribute('aria-hidden', 'true');
              const path = document.createElementNS(svgNS, 'path');
              // Heavy upward arrow, bold stroke baked into the fill path so
              // it reads as chunky even at small sizes.
              path.setAttribute(
                'd',
                'M12 3.5L5 11h4v9h6v-9h4z'
              );
              path.setAttribute('fill', 'currentColor');
              svg.appendChild(path);
              icon.appendChild(svg);

              const name = document.createElement('span');
              name.className = 'nav-hint-pill-name';
              entry.root.appendChild(icon);
              entry.root.appendChild(name);
              entry._pill = { icon, svg, name };
              entry._pillBuilt = true;
              entry._pillVersion = PILL_MARKUP_VERSION;
              entry._pillLastMeta = 0;
            }
            const p = entry._pill;
            // Author-defined direction (preferred): the pill's
            // `forwardYaw` is the world-space yaw it "points to".
            // We just take the delta between the live camera yaw and
            // that target — when the camera is looking the same way
            // the pill points, delta=0 → arrow up → label "Straight".
            // Turning the camera changes the delta and the label
            // re-buckets relative to the captured "straight" direction.
            //
            // Matterport yaw convention (verified from
            // matterportService.js `_walkPath`'s `atan2(-dx, -dz)`):
            // yaw=0 faces -Z, and yaw INCREASES when the camera
            // rotates left (CCW viewed from above). Therefore turning
            // the camera left should make the destination appear to
            // the camera's right. Compute delta as
            // `cameraYaw − forwardYaw` so positive delta = "right".
            const hasManualDir = (h.forwardYaw != null);
            let arrowDeg;
            let compassKey = '';
            if (hasManualDir) {
              const cameraYaw = pose.rotation?.y || 0;
              let delta = cameraYaw - h.forwardYaw;
              // Normalize to (-180, 180].
              delta = ((delta + 540) % 360) - 180;
              arrowDeg = delta;
              const a = Math.abs(delta);
              if (a <= 45) compassKey = 'straight';
              else if (a >= 135) compassKey = 'behind';
              else compassKey = delta > 0 ? 'right' : 'left';
            } else {
              // ── Auto behaviour ─────────────────────────────────────
              // Rotate the SVG arrow to point on-screen from the pill
              // toward the POI; bucket the same angle into a compass
              // label so both agree.
              arrowDeg = h.arrowDeg || 0;
              let labelAngleDeg = worldAngleDeg;
              let poiBehind = false;
              if (h.targetSid && destPoi) {
                const poiProj = matterportService.worldToScreen(
                  { x: destX, y: destY, z: destZ },
                  pose,
                );
                if (poiProj && poiProj.inFront) {
                  const sx = poiProj.x - proj.x;
                  const sy = poiProj.y - proj.y;
                  if (sx * sx + sy * sy > 4) {
                    arrowDeg = Math.atan2(sx, -sy) * 180 / Math.PI;
                    labelAngleDeg = arrowDeg;
                  }
                } else {
                  // POI is behind the camera — label is always "Behind".
                  arrowDeg = worldAngleDeg;
                  poiBehind = true;
                }
              }
              if (poiBehind) {
                compassKey = 'behind';
              } else {
                const a = Math.abs(labelAngleDeg);
                if (a <= 45) compassKey = 'straight';
                else if (a >= 135) compassKey = 'behind';
                else compassKey = labelAngleDeg > 0 ? 'right' : 'left';
              }
            }
            const langLabels = PILL_DIRECTION_LABELS[lang] || PILL_DIRECTION_LABELS.en;
            // Compass label is no longer rendered — only the arrow and
            // destination name are shown. We keep the bucketed key in
            // scope in case a future build wants to surface it as an
            // accessibility/aria hint.
            void compassKey; void langLabels;
            const rot = `rotate(${Math.round(arrowDeg)}deg)`;
            if (p.svg.style.transform !== rot) p.svg.style.transform = rot;
            // Pill name resolution order:
            //   1. Author-supplied customName override (per language)
            //   2. Linked Target POI's label
            //   3. The pill's own label (legacy / static pills)
            let pillName = '';
            const cn = h.customName;
            if (cn) {
              pillName = cn[lang] || cn.en || cn.mr || cn.hi || '';
            }
            if (!pillName && destPoi && destPoi.labels) {
              pillName = destPoi.labels[lang]
                || destPoi.labels.en
                || destPoi.labels.mr
                || destPoi.labels.hi
                || '';
            }
            if (!pillName) pillName = labelText;
            if (p.name.textContent !== pillName) p.name.textContent = pillName;
          } else {
            // Non-pill hints retain the original arrow + label structure.
            // Rebuild only if we're transitioning away from pill markup.
            if (entry._pillBuilt) {
              entry.root.innerHTML = '';
              entry.arrow = document.createElement('span');
              entry.arrow.className = 'nav-hint-custom-arrow';
              entry.label = document.createElement('span');
              entry.label.className = 'nav-hint-custom-label';
              entry.root.appendChild(entry.arrow);
              entry.root.appendChild(entry.label);
              entry._pillBuilt = false;
              entry._pill = null;
              // Speaker children get rebuilt below.
              entry.labelTextSpan = null;
              entry.speakerBtn = null;
            }
            // Lazy-create a text span + speaker button inside the label.
            // Keeping the text in its own span lets us update labelText
            // without clobbering the speaker icon, which lives as a
            // sibling at the end of the label flex row.
            if (!entry.labelTextSpan) {
              entry.label.textContent = '';
              entry.labelTextSpan = document.createElement('span');
              entry.labelTextSpan.className = 'nav-hint-label-text';
              entry.label.appendChild(entry.labelTextSpan);
            }
            if (entry.labelTextSpan.textContent !== labelText) {
              entry.labelTextSpan.textContent = labelText;
            }

            // Click-to-play voiceover: show a small speaker icon when the
            // POI has a `voiceoverTagId` set. Click toggles narration in
            // the current UI language; subsequent clicks stop / restart.
            const voTag = isPoi ? (h.voiceoverTagId || '') : '';
            if (voTag) {
              if (!entry.speakerBtn) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'nav-hint-speaker';
                btn.setAttribute('aria-label', 'Play narration');
                // Two icons inside: a static speaker SVG (idle) and a
                // 3-bar equalizer (playing). CSS toggles which one is
                // visible based on the `is-playing` class on the button.
                btn.innerHTML =
                  '<svg class="nav-hint-speaker__idle" viewBox="0 0 24 24" aria-hidden="true">' +
                  '<path fill="currentColor" d="M3 10v4h4l5 4V6L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4.03v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z"/>' +
                  '</svg>' +
                  '<span class="nav-hint-speaker__eq" aria-hidden="true"><i></i><i></i><i></i></span>';
                btn.addEventListener('mousedown', (ev) => { ev.stopPropagation(); });
                btn.addEventListener('click', (ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  const tag = entry._voiceoverTagId;
                  if (!tag) return;
                  const lang = languageRef.current || 'mr';
                  // Toggle: stop if this tag is currently playing —
                  // either via voiceoverService (POI VO file) or via
                  // audioService.playTourNarration (footer quick-nav /
                  // guided-tour stop sharing the same narrationKey).
                  if (voiceoverService.isPlaying()
                      && voiceoverService._currentTagId === tag) {
                    voiceoverService.stop();
                  } else if (audioService.getCurrentNarrationKey?.() === tag) {
                    audioService.stopTourNarration();
                    audioService.unduckForTour?.();
                  } else {
                    // Explicit user gesture — unmute the header if it's
                    // currently muted so the click actually produces
                    // sound (the header icon syncs via 'audioMuted').
                    voiceoverService.playForTag(tag, lang, { unmute: true });
                  }
                });
                entry.speakerBtn = btn;
                entry.label.appendChild(btn);
              }
              entry._voiceoverTagId = voTag;
              entry.speakerBtn.style.display = '';
              // Reflect live playback state — when this POI's tag is
              // currently being narrated, swap the static icon for an
              // animated equalizer (matches the header mute button).
              // Two narration paths can drive this state:
              //   • voiceoverService (POI proximity / click — uses
              //     /assets/audio/voiceover/<tag>/<lang>.mp3)
              //   • audioService.playTourNarration (footer quick-nav and
              //     guided-tour stops — keyed by the stop's narrationKey)
              // A POI's voiceoverTagId may be either kind, so we OR the
              // two checks together.
              const isThisPlaying = (
                voiceoverService.isPlaying()
                && voiceoverService._currentTagId === voTag
              ) || (
                audioService.getCurrentNarrationKey?.() === voTag
              );
              entry.speakerBtn.classList.toggle('is-playing', !!isThisPlaying);
              // Also highlight the whole POI label (brighten + scale up)
              // so users can see at a glance which point is narrating.
              entry.root.classList.toggle('nav-hint-custom--narrating', !!isThisPlaying);
            } else if (entry.speakerBtn) {
              entry.speakerBtn.style.display = 'none';
              entry._voiceoverTagId = '';
              entry.root.classList.remove('nav-hint-custom--narrating');
            }
          }

          const dx = world.x - pose.position.x;
          const dy = world.y - pose.position.y;
          const dz = world.z - pose.position.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          // Tighter clamp so hints don't balloon when you walk right up to
          // them. Upper bound capped at 1.0 (= authored size).
          const rawScale = Math.max(0.55, Math.min(1.0, 3 / (dist + 2)));
          // Quantize to 0.02 steps so frame-to-frame sub-pixel shifts
          // don't visually wobble the label during auto-pan.
          const scale = Math.round(rawScale * 50) / 50;

          // Hide when the hint would project into the top/bottom safe zones
          // reserved for the floating header/footer UI.
          const ws = proj.windowSize || { w: window.innerWidth, h: window.innerHeight };
          const TOP_SAFE = 140;    // header + language buttons
          const BOTTOM_SAFE = 160; // quick-nav dock + footer
          if (proj.y < TOP_SAFE || proj.y > ws.h - BOTTOM_SAFE) {
            entry.root.style.opacity = '0';
            continue;
          }

          // Size preset — resolved from the global style for POI/pill,
          // or falls back to per-hint / default for legacy nav hints.
          const sizePreset = SIZE_MAP[style.size] || SIZE_MAP.md;
          const fontSize = style.fontSize && style.fontSize > 0
            ? style.fontSize
            : (h.fontSize && h.fontSize > 0 ? h.fontSize : sizePreset.fontSize);
          const iconSize = sizePreset.iconSize;
          const padX = sizePreset.padX;
          const padY = sizePreset.padY;

          entry.root.style.transform =
            `translate3d(${Math.round(proj.x)}px, ${Math.round(proj.y)}px, 0) translate(-50%, -50%) scale(${scale.toFixed(2)})`;
          entry.root.style.opacity = visibilityAlpha.toFixed(2);

          // Pill hints fully CSS-styled — skip the per-frame inline shape
          // block below. Size preset is applied as CSS custom properties
          // so the pill scales in line with nav arrow / POI sizing.
          // Pills also use a *fixed* scale (1.0): the generic distance
          // shrink is great for small arrow glyphs but causes perceptible
          // jitter on a wide capsule whose text width reflows.
          if (isPill) {
            const sp = SIZE_MAP[style.size] || SIZE_MAP.md;
            const nameFont = style.fontSize && style.fontSize > 0
              ? style.fontSize
              : (h.fontSize && h.fontSize > 0 ? h.fontSize : sp.fontSize);
            // Circle badge diameter is fixed by the size preset. `iconScale`
            // controls only the SVG arrow *inside* the circle: it's the
            // fraction of the badge edge-to-edge the arrow occupies
            // (1.0 = fills the circle, 0.4 = small centered glyph).
            // Tighter defaults — pills felt bulky at the old values.
            const iconPx = Math.round(sp.iconSize * 0.85);
            const iconScale = Math.max(0.3, Math.min(1.0, style.iconScale || 0.85));
            const metaFont = Math.max(10, Math.round(nameFont * (12 / 14)));
            const padXp = Math.max(8, sp.padX - 2);
            const padYp = Math.max(4, sp.padY - 2);
            entry.root.style.setProperty('--pill-name-font', `${nameFont}px`);
            entry.root.style.setProperty('--pill-meta-font', `${metaFont}px`);
            entry.root.style.setProperty('--pill-icon-size', `${iconPx}px`);
            entry.root.style.setProperty('--pill-arrow-scale', iconScale.toFixed(2));
            entry.root.style.setProperty('--pill-pad-x', `${padXp}px`);
            entry.root.style.setProperty('--pill-pad-y', `${padYp}px`);

            // ── Apply global pill style ──────────────────────────────
            const pillBg = hexToRgba(style.bgColor || '#0a0a0c', style.bgOpacity ?? 0.82);
            entry.root.style.background = pillBg;
            const pillParts = entry._pill;
            const nameColor = style.textColor || '#ffffff';
            if (pillParts && pillParts.name) {
              pillParts.name.style.color = nameColor;
              pillParts.name.style.fontWeight = String(style.fontWeight || 700);
            }
            if (pillParts && pillParts.icon) {
              // Keep the default orange gradient from the stylesheet.
              pillParts.icon.style.background = '';
              pillParts.icon.style.boxShadow = '';
              pillParts.icon.style.display = '';
            }
            const shapeKey = style.shape || 'pill';
            const SHAPE_RADIUS = { pill: '999px', rect: '10px', circle: '999px', pin: '20px 20px 20px 4px', none: '0' };
            entry.root.style.borderRadius = SHAPE_RADIUS[shapeKey] || '999px';
            if (shapeKey === 'none') {
              entry.root.style.background = 'transparent';
              entry.root.style.boxShadow = 'none';
              entry.root.style.border = 'none';
              entry.root.style.backdropFilter = 'none';
              entry.root.style.webkitBackdropFilter = 'none';
            } else {
              entry.root.style.boxShadow = '';
              entry.root.style.border = '';
              entry.root.style.backdropFilter = '';
              entry.root.style.webkitBackdropFilter = '';
            }

            // Override the distance scale with a fixed 1.0 so the pill
            // doesn't grow/shrink as the camera moves.
            entry.root.style.transform =
              `translate3d(${Math.round(proj.x)}px, ${Math.round(proj.y)}px, 0) translate(-50%, -50%)`;
            continue;
          }

          // Apply shape preset to label (uses resolved global style).
          const shape = style.shape || 'pill';
          const bg = hexToRgba(style.bgColor || '#000', style.bgOpacity ?? 0.75);
          const baseLabel = entry.label.style;
          baseLabel.color = style.textColor || '#fff';
          baseLabel.fontSize = `${fontSize}px`;
          baseLabel.fontWeight = style.fontWeight || 600;
          baseLabel.padding = `${padY}px ${padX}px`;

          // Clear styles that may differ between shapes
          baseLabel.background = '';
          baseLabel.borderRadius = '';
          baseLabel.boxShadow = '';
          baseLabel.position = '';
          baseLabel.width = '';
          baseLabel.minWidth = '';
          baseLabel.height = '';
          baseLabel.display = '';
          baseLabel.justifyContent = '';
          baseLabel.alignItems = '';
          baseLabel.textAlign = '';
          baseLabel.textShadow = '';
          baseLabel.border = '';
          // reset any pin pseudo via className toggle
          entry.label.className = `nav-hint-custom-label nav-hint-shape-${shape}`;

          // Warm-orange hairline border matches the directional-pill accent
          // and keeps all three hint types visually unified.
          const borderColor = `rgba(239, 127, 26, 0.45)`;

          if (shape === 'circle') {
            // Circle badge — grows to a capsule when the label is longer
            // than the base diameter so text is never clipped to a dot.
            const s = Math.max(iconSize + 14, fontSize * 2.4);
            baseLabel.background = bg;
            baseLabel.borderRadius = `${s}px`;
            baseLabel.minWidth = `${s}px`;
            baseLabel.height = `${s}px`;
            baseLabel.display = 'inline-flex';
            baseLabel.alignItems = 'center';
            baseLabel.justifyContent = 'center';
            baseLabel.padding = `0 ${Math.round(s * 0.35)}px`;
            baseLabel.border = `1px solid ${borderColor}`;
          } else if (shape === 'rect') {
            baseLabel.background = bg;
            baseLabel.borderRadius = '4px';
            baseLabel.border = `1px solid ${borderColor}`;
          } else if (shape === 'pin') {
            baseLabel.background = bg;
            baseLabel.borderRadius = '16px';
            baseLabel.position = 'relative';
            baseLabel.border = `1px solid ${borderColor}`;
          } else if (shape === 'none') {
            baseLabel.background = 'transparent';
            baseLabel.textShadow = '0 1px 6px rgba(0,0,0,0.9), 0 0 3px rgba(0,0,0,0.85)';
            baseLabel.padding = '0';
            baseLabel.border = 'none';
          } else {
            // pill
            baseLabel.background = bg;
            baseLabel.borderRadius = '999px';
            baseLabel.border = `1px solid ${borderColor}`;
          }

          // POI markers are text-only now — icon glyph is always hidden.
          // Nav arrows (legacy) retain the showIcon/icon controls.
          const showIcon = !isPoi && h.showIcon !== false && h.icon !== '';
          if (showIcon) {
            const iconChar = h.icon || '➤';
            if (entry.arrow.textContent !== iconChar) entry.arrow.textContent = iconChar;
            entry.arrow.style.display = 'inline-block';
            entry.arrow.style.fontSize = `${iconSize}px`;
            entry.arrow.style.transform = `rotate(${h.arrowDeg || 0}deg)`;
            entry.arrow.style.color = h.iconColor || style.textColor || '#fff';
          } else {
            entry.arrow.style.display = 'none';
          }
        }

        // Remove stale nodes
        for (const [id, entry] of nodes) {
          if (!seen.has(id)) {
            entry.root.remove();
            nodes.delete(id);
          }
        }

        // Tear down audio for hints that were deleted or had audio removed.
        const audioMap = audioNodesRef.current;
        const liveIds = new Set(list.filter((h) => h && h.audioSrc).map((h) => h.id));
        for (const [id, a] of audioMap) {
          if (!liveIds.has(id)) {
            try { a.el.pause(); a.el.src = ''; a.el.remove(); } catch (_) {}
            audioMap.delete(id);
          }
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('quickNavArrival', onQuickNavArrival);
      window.removeEventListener('narrationStarted', onNarrationStarted);
    };
  }, [active, language]);

  if (!active) return null;

  return (
    <div
      ref={customHostRef}
      className="nav-hint-custom-host"
      style={{ zIndex: Z_INDEX.NAV_HINT_CUSTOM }}
      aria-hidden="true"
    />
  );
};

// Small util: convert a #rrggbb + opacity into an rgba() string.
function hexToRgba(hex, alpha) {
  try {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3
      ? h.split('').map((c) => c + c).join('')
      : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch (_) {
    return `rgba(0,0,0,${alpha})`;
  }
}

export default NavigationHints;
